import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Three ways to set a story on paper:
//   hero    — plate first, headline centred underneath, terracotta rule above the kicker
//   default — a warm card of stock with a rounded plate at the top
//   compact — a text-first line with a small square plate at the right
export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up"
            >
                <div className="overflow-hidden rounded-2xl border border-line bg-surface-raised shadow-card">
                    <div className="aspect-[16/9] overflow-hidden">
                        {article.thumbnail && (
                            <img
                                src={article.thumbnail}
                                alt={article.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            />
                        )}
                    </div>
                </div>

                <div className="max-w-3xl mx-auto px-2 text-center mt-9 sm:mt-12">
                    <span className="block h-0.5 w-12 mx-auto rounded-full bg-accent" />
                    <span className="eyebrow block mt-5 text-accent">{label}</span>
                    <h2 className="font-display text-[2rem] sm:text-[2.9rem] lg:text-[3.35rem] font-semibold leading-[1.12] mt-4 text-foreground group-hover:text-accent transition-colors duration-300">
                        {article.title}
                    </h2>
                    {article.excerpt && (
                        <p className="mt-6 text-[17px] text-dim leading-[1.75] max-w-2xl mx-auto">
                            {article.excerpt}
                        </p>
                    )}
                    <div className="mt-7 flex items-center justify-center gap-3 font-mono text-[11px] text-dim">
                        {article.author && <span className="truncate max-w-[240px]">{article.author}</span>}
                        {article.author && <span className="w-1 h-1 rounded-full bg-line-strong" />}
                        <span>{timeAgo(article.published_at)}</span>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-5 py-5 items-start"
            >
                <div className="flex-1 min-w-0">
                    <span className="eyebrow !text-[10px] text-accent">{label}</span>
                    <h3 className="font-display text-[17px] font-semibold leading-[1.35] mt-2 text-foreground group-hover:text-accent transition-colors duration-200">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] text-dim mt-2 block">{timeAgo(article.published_at)}</span>
                </div>
                {article.thumbnail && (
                    <div className="w-[92px] h-[76px] shrink-0 overflow-hidden rounded-lg border border-line bg-surface-raised">
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group block animate-fade-up rounded-2xl border border-line bg-surface shadow-card overflow-hidden transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-line-strong hover:shadow-lift"
        >
            <div className="aspect-[3/2] overflow-hidden bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="p-6">
                <span className="eyebrow !text-[10px] text-accent">{label}</span>
                <h3 className="font-display text-[1.3rem] font-semibold leading-[1.28] mt-2.5 text-foreground group-hover:text-accent transition-colors duration-200">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-3 text-[15px] text-dim leading-[1.7] line-clamp-3">{article.excerpt}</p>
                )}
                <div className="mt-5 pt-4 border-t border-line flex items-center gap-2.5 font-mono text-[11px] text-dim">
                    {article.author && <span className="truncate max-w-[160px]">{article.author}</span>}
                    {article.author && <span className="w-1 h-1 rounded-full bg-line-strong" />}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
